// react
import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import EditRoundedIcon from '@mui/icons-material/EditRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import type { GetDeviceDto } from '@/api/generated/recalc/endpoints/v2/devices';
import { usePutDeviceImageMutation } from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import { WritableOnly } from '@/components/access-control/AccessGuard';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface DeviceImageCardProps {
  device?: GetDeviceDto | null;
}

function DefaultDeviceIllustration() {
  return (
    <Box
      component="svg"
      viewBox="0 0 220 120"
      role="img"
      aria-hidden="true"
      sx={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <linearGradient id="deviceCardGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#dff4ff" />
          <stop offset="100%" stopColor="#eef7ff" />
        </linearGradient>
      </defs>

      <rect width="220" height="120" rx="24" fill="url(#deviceCardGradient)" />

      <rect
        x="64"
        y="18"
        width="104"
        height="88"
        rx="18"
        fill="#ffffff"
        stroke="#8cc9f5"
        strokeWidth="6"
      />

      <rect x="80" y="34" width="72" height="38" rx="8" fill="#dff4ff" />

      <circle cx="92" cy="88" r="6" fill="#57b0ea" />
      <circle cx="116" cy="88" r="6" fill="#57b0ea" />
      <circle cx="140" cy="88" r="6" fill="#57b0ea" />

      <path
        d="M169 48c12 0 22 10 22 22"
        fill="none"
        stroke="#57b0ea"
        strokeWidth="7"
        strokeLinecap="round"
      />

      <path
        d="M169 32c21 0 38 17 38 38"
        fill="none"
        stroke="#9ed4f7"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </Box>
  );
}

const DeviceImageCard = ({ device }: DeviceImageCardProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [putDeviceImage, { isLoading: isUploadingImage }] =
    usePutDeviceImageMutation();

  const imageUrl = useMemo(() => {
    if (!device?.imageUrl) {
      return null;
    }

    if (device.imageHash == null) {
      return device.imageUrl;
    }

    const separator = device.imageUrl.includes('?') ? '&' : '?';

    return `${device.imageUrl}${separator}v=${encodeURIComponent(
      String(device.imageHash),
    )}`;
  }, [device]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file || !device?.deviceId) {
      return;
    }

    const result = await putDeviceImage({
      deviceId: device.deviceId,
      body: { image: file },
    });

    if (!('error' in result)) {
      setImageFailed(false);
      dispatch(
        showSnackbar({
          message: t('devices.imageUploadSuccess'),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card>
      <CardContent>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />

        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Stack spacing={0.25}>
                <Typography variant="h6">
                  {t('devices.image', { defaultValue: 'Image' })}
                </Typography>
              </Stack>
            </Stack>

            {device?.deviceId ? (
              <WritableOnly>
                <Tooltip title={t('common.edit')}>
                  <IconButton
                    onClick={() =>
                      alert('Edit image functionality not implemented yet.')
                    }
                    size="small"
                    disabled={isUploadingImage}
                  >
                    <EditRoundedIcon />
                  </IconButton>
                </Tooltip>
              </WritableOnly>
            ) : null}
          </Stack>

          <Box sx={{ position: 'relative' }}>
            {imageUrl && !imageFailed ? (
              <Box sx={{ width: '100%' }}>
                <img
                  src={imageUrl}
                  style={{
                    width: '100%',
                    height: '40vh',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  onError={() => setImageFailed(true)}
                  alt={t('devices.image', { defaultValue: 'Image' })}
                />
              </Box>
            ) : (
              <Stack
                spacing={2}
                alignItems="center"
                justifyContent="center"
                sx={{ px: 3, py: 4, minHeight: 280 }}
              >
                <Box sx={{ width: '100%', maxWidth: 420 }}>
                  <DefaultDeviceIllustration />
                </Box>
                <Stack spacing={0.5} alignItems="center">
                  <Typography variant="subtitle1">
                    {t('common.noImage')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('devices.imageEmptyDescription', {
                      defaultValue:
                        'Upload your own image to make this device easier to recognize.',
                    })}
                  </Typography>
                </Stack>
              </Stack>
            )}

            {isUploadingImage ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.6)',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeviceImageCard;
